/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Raycaster } from 'three';
import { MouseOverTracker } from './MouseOverTracker';
/**
 * Acts as interaction (mouse over/down/up ..) controller for 3d objects.
 *
 * Emits the following syntetic events to the internal interative objects list:
 * - `highlight`
 * - `unhighlight`
 * - `click`
 *
 * Add your objects to the list via `objectInteractor.addInteractiveObject(obj,...)`
 *
 */
export class ObjectInteractor {
    constructor(_stage, _cameraName, _viewportName, _extraEventPayload) {
        this._stage = _stage;
        this._cameraName = _cameraName;
        this._viewportName = _viewportName;
        this._extraEventPayload = _extraEventPayload;
        this._raycaster = new Raycaster();
        this._interactiveObjects = [];
        this._removeListeners = [];
        this._mouseOverTracker = new MouseOverTracker({
            onMouseOver(intersection) {
                const event = Object.assign({ type: ObjectInteractor.HIGHLIGHT, intersection }, _extraEventPayload);
                intersection.object.dispatchEvent(event);
            },
            onMouseOut(obj3d) {
                const event = Object.assign({ type: ObjectInteractor.UNHIGHLIGHT }, _extraEventPayload);
                obj3d.dispatchEvent(event);
            },
        });
        this._registerEventListeners();
    }
    dispose() {
        this._removeListeners.forEach(([type, listener]) => {
            this._stageDomElement.removeEventListener(type, listener);
        });
        this._removeListeners.length = 0;
    }
    pause(pause = true) {
        if (pause && this._removeListeners.length > 0) {
            this.dispose();
        }
        else if (!pause && this._removeListeners.length === 0) {
            this._registerEventListeners();
        }
    }
    /**
     * Returns `false` if the `ObjectInteractor` is paused.
     */
    get isActive() {
        return this._removeListeners.length > 0;
    }
    /**
     * Add one or more objects to the interactive list.
     * All interactive objects will receive the `highlight`, `unhighlight` and `click` events.
     */
    addInteractiveObject(...obj) {
        this._interactiveObjects.push(...obj);
    }
    /**
     * Add extra payload to all events.
     */
    setExtraEventPayload(payload) {
        this._extraEventPayload = Object.assign({}, this._extraEventPayload, payload);
    }
    get _camera() {
        return this._stage.getCamera(this._cameraName);
    }
    _raycast(pos) {
        this._raycaster.setFromCamera({ x: pos.mousex, y: pos.mousey }, this._camera);
        return this._raycaster.intersectObjects(this._interactiveObjects, true);
    }
    get _stageDomElement() {
        return this._stage.getRenderer().domElement;
    }
    _registerEventListeners() {
        const subscribeTo = (eventName, fn) => {
            const listener = this._stage.readRelMousePosByViewport(this._viewportName, fn);
            this._stageDomElement.addEventListener(eventName, listener, { passive: true });
            this._removeListeners.push([eventName, listener]);
        };
        subscribeTo('mousemove', (pos) => {
            const intersections = this._raycast(pos);
            this._mouseOverTracker.emit(intersections.slice(0, 1));
        });
        subscribeTo('mousedown', (pos) => {
            const intersection = this._raycast(pos)[0];
            if (intersection) {
                const event = { type: ObjectInteractor.CLICK, detail: Object.assign({ intersection }, this._extraEventPayload) };
                intersection.object.dispatchEvent(event);
            }
        });
    }
}
ObjectInteractor.HIGHLIGHT = 'highlight';
ObjectInteractor.UNHIGHLIGHT = 'unhighlight';
ObjectInteractor.CLICK = 'click';
//# sourceMappingURL=ObjectInteractor.js.map